/**
 * ✅ FinalApproveModal Component
 * Fecha: 30 de octubre de 2025
 * 
 * Modal para que el Project Manager apruebe definitivamente una solicitud
 * después de revisar los salarios asignados por RH.
 * Estado requerido: en_revision_salarios → aprobada
 */

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState } from 'react'
import { X, CheckCircle2, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import type { JobRequest } from '@/services/api'
import { useJobRequestWorkflow } from '@/hooks/useJobRequestWorkflow'
import {
  formatJobRequestArea,
  formatJobRequestPriority,
  formatJobRequestSede
} from '@/features/rh/lib/jobRequestDisplay'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface FinalApproveModalProps {
  jobRequest: JobRequest
  isOpen: boolean
  onClose: () => void
  onSuccess: (updated: JobRequest) => void
}

const FinalApproveModal: React.FC<FinalApproveModalProps> = ({
  jobRequest,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [comentarios, setComentarios] = useState('')
  const { finalApprove, loading, error } = useJobRequestWorkflow()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const result = await finalApprove(jobRequest.id, { 
      comentarios: comentarios.trim() || undefined 
    })

    if (result) {
      toast.success('Solicitud aprobada. Vacante activa creada exitosamente.')
      onSuccess(result)
      handleClose()
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
      setComentarios('')
    }
  }

  const formatCurrency = (value: string | null): string => {
    if (!value) return 'No especificado'
    const num = parseFloat(value)
    if (isNaN(num)) return 'No especificado'
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(num)
  }

  if (!isOpen) return null

  return (
    <CapaDeModal>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Aprobar definitivamente
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            {/* Información de la solicitud */}
            <div>
              <h3 className="font-medium text-black mb-2">
                {jobRequest.puestoSolicitar}
              </h3>
              <div className="text-sm text-black space-y-1">
                <p><strong>Departamento/Área:</strong> {formatJobRequestArea(jobRequest.area?.nombre || jobRequest.areaVacante)}</p>
                <p><strong>Sede:</strong> {formatJobRequestSede(jobRequest.sede)}</p>
                <p><strong>Prioridad:</strong> {formatJobRequestPriority(jobRequest.prioridad)}</p>
                <p><strong>Número de vacantes:</strong> {jobRequest.numeroPlazas}</p>
              </div>
            </div>

            {/* Información salarial asignada por RH */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h4 className="font-medium text-blue-900">Rangos salariales asignados</h4>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-black font-medium">Salario mínimo</p>
                  <p className="text-black text-lg font-semibold">
                    {formatCurrency(jobRequest.salarioMinimo)}
                  </p>
                </div>
                <div>
                  <p className="text-black font-medium">Salario máximo</p>
                  <p className="text-black text-lg font-semibold">
                    {formatCurrency(jobRequest.salarioMaximo)}
                  </p>
                </div>
              </div>
              {jobRequest.prestaciones && (
                <div className="mt-3 pt-3 border-t border-gray">
                  <p className="text-black font-medium text-sm mb-1">Prestaciones</p>
                  <p className="text-black text-sm">{jobRequest.prestaciones}</p>
                </div>
              )}
              {jobRequest.comentariosAsignacionSalarios && (
                <div className="mt-3 pt-3 border-t border-gray">
                  <p className="text-blue-600 font-medium text-sm mb-1">Comentarios de RH</p>
                  <p className="text-blue-800 text-sm">{jobRequest.comentariosAsignacionSalarios}</p>
                </div>
              )}
            </div>

            {/* Advertencia */}
            <div>
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-black">
                  <p className="font-medium mb-1">Al aprobar definitivamente:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>La solicitud cambiará a estado <strong>"Vacante Activa"</strong></li>
                    <li>Se creará automáticamente la vacante activa</li>
                    <li>RH podrá comenzar a recibir candidatos</li>
                    <li>Esta acción <strong>no se puede deshacer</strong></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Campo de comentarios */}
            <div>
              <label htmlFor="comentarios" className="block text-sm font-medium text-gray-700 mb-2">
                Comentarios de aprobación (Opcional)
              </label>
              <textarea
                id="comentarios"
                value={comentarios}
                onChange={(e) => setComentarios(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Agrega comentarios finales sobre la aprobación..."
                disabled={loading}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 bg-gray-50 sticky bottom-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className={estiloDeBoton('exito')}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Aprobando...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Aprobar definitivamente</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </CapaDeModal>
  )
}

export default FinalApproveModal
